import { ChartInfoKind } from '@frontend/chart/types';
import { MessageDescriptor } from 'react-intl';

export interface ChartCardInfoMessages {
    dataDescription: MessageDescriptor;
    conclusions: MessageDescriptor;
}

const info = (
    kind: ChartInfoKind,
    dataDescription: MessageDescriptor['defaultMessage'],
    conclusions: MessageDescriptor['defaultMessage']
): ChartCardInfoMessages => ({
    dataDescription: {
        id: `Statistics.ChartInfo.${kind}.DataDescription`,
        description: `Explains what data is shown in the ${kind} chart or dashboard card`,
        defaultMessage: dataDescription
    },
    conclusions: {
        id: `Statistics.ChartInfo.${kind}.Conclusions`,
        description: `Explains what general conclusions can be drawn from the ${kind} chart or dashboard card`,
        defaultMessage: conclusions
    }
});

export const CHART_CARD_INFO_MESSAGES: Record<ChartInfoKind, ChartCardInfoMessages> = {
    [ChartInfoKind.TRANSACTION_VOLUME_LINE]: info(
        ChartInfoKind.TRANSACTION_VOLUME_LINE,
        'The number of completed transactions over time, grouped by day.',
        'You can spot trends in transaction activity, compare busy and quiet periods, and see whether volume is increasing or decreasing.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BAR,
        'The number of transactions per time period, shown as vertical bars.',
        'You can compare transaction volume across periods and identify which intervals had the highest or lowest activity.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BY_STATE_LIST]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BY_STATE_LIST,
        'A ranked list of transaction counts grouped by their current state (for example awaiting pick-up or completed).',
        'You can see which transaction states make up the bulk of your workload and where bottlenecks may be building up.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BY_SENDER_LIST]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BY_SENDER_LIST,
        'A ranked list of senders by number of PuDo transactions over the selected period.',
        'You can identify your most active senders and see who is driving the highest outbound transaction volume.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BY_RECEIVER_LIST]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BY_RECEIVER_LIST,
        'A ranked list of receivers by number of PuDo transactions over the selected period.',
        'You can identify your most active receivers and see who is receiving the highest transaction volume.'
    ),
    [ChartInfoKind.TRANSACTION_TIMERS_DURATION_BY_USER_DROP_OFF_LIST]: info(
        ChartInfoKind.TRANSACTION_TIMERS_DURATION_BY_USER_DROP_OFF_LIST,
        'A ranked list of users with the longest average time to complete a drop-off over the selected period.',
        'You can identify who is slowest to drop off and follow up where turnaround is lagging.'
    ),
    [ChartInfoKind.TRANSACTION_TIMERS_DURATION_BY_USER_PICK_UP_LIST]: info(
        ChartInfoKind.TRANSACTION_TIMERS_DURATION_BY_USER_PICK_UP_LIST,
        'A ranked list of users with the longest average time to complete a pick-up over the selected period.',
        'You can identify who is slowest to pick up and follow up where packages sit too long.'
    ),
    [ChartInfoKind.TRANSACTION_ACTIVE_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_ACTIVE_VOLUME_BAR,
        'The number of active (in-progress) transactions per time period.',
        'You can monitor how many transactions are open at any given time and spot peaks in ongoing activity.'
    ),
    [ChartInfoKind.TRANSACTION_DROP_OFF_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_DROP_OFF_VOLUME_BAR,
        'The number of drop-off transactions per time period, split by completion status.',
        'You can compare pending and completed drop-offs over time and see whether drop-off throughput is improving.'
    ),
    [ChartInfoKind.TRANSACTION_PICK_UP_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_PICK_UP_VOLUME_BAR,
        'The number of pick-up transactions per time period, split by completion status.',
        'You can compare pending and completed pick-ups over time and identify periods with slower pick-up completion.'
    ),
    [ChartInfoKind.TRANSACTION_DROP_OFF_PICK_UP_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_DROP_OFF_PICK_UP_VOLUME_BAR,
        'The combined volume of drop-off and pick-up transactions per time period.',
        'You can compare overall PuDo activity over time and see whether drop-offs or pick-ups dominate.'
    ),
    [ChartInfoKind.TRANSACTION_PURCHASE_ORDER_VOLUME_BAR]: info(
        ChartInfoKind.TRANSACTION_PURCHASE_ORDER_VOLUME_BAR,
        'The number of purchase-order-related transactions per time period.',
        'You can track ordering activity over time and spot seasonal or weekly patterns in purchase orders.'
    ),
    [ChartInfoKind.TRANSACTION_TIMER_WAITING_DAYS_DONUT]: info(
        ChartInfoKind.TRANSACTION_TIMER_WAITING_DAYS_DONUT,
        'The distribution of how long transactions have been waiting, grouped into day ranges.',
        'You can see whether most transactions are resolved quickly or if a significant share has been waiting for several days.'
    ),
    [ChartInfoKind.TRANSACTION_TIMER_WAITING_DAYS_STACKED_BAR]: info(
        ChartInfoKind.TRANSACTION_TIMER_WAITING_DAYS_STACKED_BAR,
        'The number of transactions in each waiting-day range, shown as stacked segments per period.',
        'You can track how the waiting-time mix shifts over time and whether long-waiting transactions are growing.'
    ),
    [ChartInfoKind.TRANSACTION_TIMER_DURATION_METRIC]: info(
        ChartInfoKind.TRANSACTION_TIMER_DURATION_METRIC,
        'The average time transactions spend in a pending state, compared to the previous period.',
        'You can assess whether drop-off or pick-up turnaround is improving or slowing down compared to last month.'
    ),
    [ChartInfoKind.TRANSACTION_TIMER_ACTIVITY_WEEKDAY_HEATMAP]: info(
        ChartInfoKind.TRANSACTION_TIMER_ACTIVITY_WEEKDAY_HEATMAP,
        'Transaction activity intensity by day of the week over several months, shown as a colour grid.',
        'You can identify which weekdays are busiest and spot recurring weekly patterns in transaction flow.'
    ),
    [ChartInfoKind.TRANSACTION_TIMER_ACTIVITY_HOUR_DAY_HEATMAP]: info(
        ChartInfoKind.TRANSACTION_TIMER_ACTIVITY_HOUR_DAY_HEATMAP,
        'Transaction activity intensity by hour of day and day of week, shown as a colour grid.',
        'You can discover peak hours and quiet periods to help plan staffing or capacity.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BY_SLOT_DIMENSION_DONUT]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BY_SLOT_DIMENSION_DONUT,
        'The share of transactions per SLOT size category (for example small, medium, large).',
        'You can see which SLOT sizes are used most and whether compartment capacity matches demand.'
    ),
    [ChartInfoKind.TRANSACTION_VOLUME_BY_SPOT_DONUT]: info(
        ChartInfoKind.TRANSACTION_VOLUME_BY_SPOT_DONUT,
        'The share of PuDo transactions grouped by SPOT over the selected period.',
        'You can see which SPOTs handle the most traffic and where capacity or staffing may need attention.'
    ),
    [ChartInfoKind.DAILY_TURNOVER_RATE_LINE]: info(
        ChartInfoKind.DAILY_TURNOVER_RATE_LINE,
        'The average number of complete drop-off and pick-up cycles per SLOT per day over time.',
        'You can gauge how efficiently SLOTs are being reused and whether turnover is improving or declining.'
    ),
    [ChartInfoKind.STALE_PICK_UP_TRANSACTIONS_LIST]: info(
        ChartInfoKind.STALE_PICK_UP_TRANSACTIONS_LIST,
        'A list of pick-up transactions that have been waiting longer than expected, with sender, receiver, and waiting time.',
        'You can identify transactions that need follow-up and prioritise outreach to receivers who have not picked up yet.'
    ),
    [ChartInfoKind.STALE_DROP_OFF_TRANSACTIONS_LIST]: info(
        ChartInfoKind.STALE_DROP_OFF_TRANSACTIONS_LIST,
        'A list of drop-off transactions that have been waiting longer than expected, with sender, receiver, and waiting time.',
        'You can identify transactions that need follow-up and prioritise outreach to senders who have not dropped off yet.'
    ),
    [ChartInfoKind.SPOT_FILL_RATE_LINE]: info(
        ChartInfoKind.SPOT_FILL_RATE_LINE,
        'SPOT SLOT occupancy over time. On dashboards with a capacity line, values are occupied SLOT counts versus total capacity.',
        'You can track whether SPOT utilisation is rising or falling and spot periods of high or low occupancy.'
    ),
    [ChartInfoKind.SPOT_FILL_RATE_METRIC]: info(
        ChartInfoKind.SPOT_FILL_RATE_METRIC,
        'The current average daily SLOT fill rate compared to the previous period.',
        'You can quickly see whether overall SPOT utilisation is improving or declining compared to last month.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_LINE]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_LINE,
        'The number of dispensed packages over time, grouped by day.',
        'You can spot trends in vending activity and compare busy and quiet periods.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_PRODUCT_LIST]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_PRODUCT_LIST,
        'A ranked list of products by dispensed package volume.',
        'You can see which products users consume the most and prioritise restocking for high-demand items.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_PRODUCT_MONTHLY_LIST]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_PRODUCT_MONTHLY_LIST,
        'Products ranked by month-over-month change in dispensed package volume.',
        'You can spot which products are gaining or losing popularity and adjust stock levels accordingly.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_USER_LIST]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_BY_USER_LIST,
        'A ranked list of users by dispensed package volume.',
        'You can identify your most active consumers and understand usage patterns across users.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_WEEKDAY_HEATMAP]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_WEEKDAY_HEATMAP,
        'Dispensed package volume by day of the week over several months, shown as a colour grid.',
        'You can identify which weekdays have the highest vending activity and plan replenishment accordingly.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_VOLUME_HOUR_BAR]: info(
        ChartInfoKind.VENDING_PACKAGE_VOLUME_HOUR_BAR,
        'Dispensed package volume grouped by hour of the day.',
        'You can discover peak dispensing hours and quiet periods to optimise stocking and maintenance windows.'
    ),
    [ChartInfoKind.FILL_RATE_BY_PRODUCT_LIST]: info(
        ChartInfoKind.FILL_RATE_BY_PRODUCT_LIST,
        'A ranked list of products by current fill rate percentage.',
        'You can see which products are running low or overstocked and prioritise replenishment.'
    ),
    [ChartInfoKind.FILL_RATE_BY_PRODUCT_LINE]: info(
        ChartInfoKind.FILL_RATE_BY_PRODUCT_LINE,
        'The fill rate of a product over time, expressed as a percentage.',
        'You can track whether a product stays adequately stocked or frequently runs low.'
    ),
    [ChartInfoKind.FILL_RATE_BY_SPOT_LIST]: info(
        ChartInfoKind.FILL_RATE_BY_SPOT_LIST,
        'A ranked list of SPOTs by current fill rate percentage.',
        'You can compare utilisation across locations and identify SPOTs that need restocking or have excess capacity.'
    ),
    [ChartInfoKind.FILL_RATE_TOTAL_METRIC]: info(
        ChartInfoKind.FILL_RATE_TOTAL_METRIC,
        'The overall fill rate percentage across all products at this SPOT.',
        'You can see how fully stocked the SPOT is overall and whether capacity is being used efficiently.'
    ),
    [ChartInfoKind.FILL_RATE_STATUS_DONUT]: info(
        ChartInfoKind.FILL_RATE_STATUS_DONUT,
        'The number of products in each fill rate status category (under, near, or over the target maximum).',
        'You can quickly assess overall stocking health and see how many products need attention.'
    ),
    [ChartInfoKind.CONSUMPTION_RATE_BY_PRODUCT_LIST]: info(
        ChartInfoKind.CONSUMPTION_RATE_BY_PRODUCT_LIST,
        'A ranked list of products by consumption rate.',
        'You can identify fast-moving products and adjust replenishment schedules to match demand.'
    ),
    [ChartInfoKind.PERIOD_CHANGE_METRIC]: info(
        ChartInfoKind.PERIOD_CHANGE_METRIC,
        'The product with the largest increase or decrease in consumption compared to the previous period.',
        'You can quickly spot the biggest mover up or down and investigate changes in demand.'
    ),
    [ChartInfoKind.PRODUCTS_NEEDING_REFILL_LIST]: info(
        ChartInfoKind.PRODUCTS_NEEDING_REFILL_LIST,
        'Products currently below their minimum stock level, ranked by urgency.',
        'You can prioritise which products to refill first based on how far below minimum they are.'
    ),
    [ChartInfoKind.VENDING_PRODUCT_POPULARITY_LIST]: info(
        ChartInfoKind.VENDING_PRODUCT_POPULARITY_LIST,
        'Products with the largest month-over-month increase and decrease in dispensed volume.',
        'You can see which products are trending up or down in popularity and adjust assortment or stock levels.'
    ),
    [ChartInfoKind.VENDING_TIME_TO_STOCKOUT_LIST]: info(
        ChartInfoKind.VENDING_TIME_TO_STOCKOUT_LIST,
        'Products ranked by estimated days until they run out of stock at the current consumption rate.',
        'You can prioritise replenishment for products closest to stockout.'
    ),
    [ChartInfoKind.VENDING_PACKAGE_TIMERS_DURATION_AUDIT_PENDING_BY_PRODUCT_LIST]: info(
        ChartInfoKind.VENDING_PACKAGE_TIMERS_DURATION_AUDIT_PENDING_BY_PRODUCT_LIST,
        'A ranked list of products with the longest open package audit-pending waits for vending returns.',
        'You can see which returned products have been waiting for review the longest and clear the oldest backlog first.'
    ),
    [ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_BY_PRODUCT_LIST]: info(
        ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_BY_PRODUCT_LIST,
        'A ranked list of products by outbound lending volume.',
        'You can see which equipment or items are borrowed most frequently.'
    ),
    [ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_BY_PRODUCT_MONTHLY_LIST]: info(
        ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_BY_PRODUCT_MONTHLY_LIST,
        'Products ranked by month-over-month change in outbound lending volume.',
        'You can spot which items are gaining or losing demand in your lending program.'
    ),
    [ChartInfoKind.LENDING_EQUIPMENT_USES_VOLUME_BY_PRODUCT_LIST]: info(
        ChartInfoKind.LENDING_EQUIPMENT_USES_VOLUME_BY_PRODUCT_LIST,
        'Products ranked by how many times equipment was used (borrowed) in the selected period.',
        'You can see which items are used most and least and adjust fleet size or assortment.'
    ),
    [ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_WEEKDAY_HEATMAP]: info(
        ChartInfoKind.LENDING_OUTBOUND_PACKAGE_VOLUME_WEEKDAY_HEATMAP,
        'Outbound lending volume by day of the week over several months, shown as a colour grid.',
        'You can identify which weekdays have the highest borrowing activity.'
    ),
    [ChartInfoKind.LENDING_RETURN_PACKAGE_VOLUME_WEEKDAY_HEATMAP]: info(
        ChartInfoKind.LENDING_RETURN_PACKAGE_VOLUME_WEEKDAY_HEATMAP,
        'Return volume by day of the week over several months, shown as a colour grid.',
        'You can identify which weekdays have the highest return activity.'
    ),
    [ChartInfoKind.EQUIPMENT_STATE_DONUT]: info(
        ChartInfoKind.EQUIPMENT_STATE_DONUT,
        'The share of equipment in each state (for example available, in use, or unavailable).',
        'You can see the overall health of your equipment fleet and how much is currently lendable.'
    ),
    [ChartInfoKind.EQUIPMENT_STATE_LIST]: info(
        ChartInfoKind.EQUIPMENT_STATE_LIST,
        'Equipment items grouped and counted by their current state.',
        'You can drill into which items are available, in use, or blocked and take action on unavailable units.'
    ),
    [ChartInfoKind.EQUIPMENT_CERTIFICATIONS_DONUT]: info(
        ChartInfoKind.EQUIPMENT_CERTIFICATIONS_DONUT,
        'The share of equipment by certification status (valid, expiring soon, or expired).',
        'You can assess compliance risk and prioritise recertification for items nearing expiry.'
    ),
    [ChartInfoKind.EQUIPMENT_DURATION_BY_PRODUCT_LIST]: info(
        ChartInfoKind.EQUIPMENT_DURATION_BY_PRODUCT_LIST,
        'A ranked list of products by average time equipment spends in use.',
        'You can see which items tend to be kept longer and adjust lending policies or availability.'
    ),
    [ChartInfoKind.EQUIPMENT_DURATION_BY_EQUIPMENT_LIST]: info(
        ChartInfoKind.EQUIPMENT_DURATION_BY_EQUIPMENT_LIST,
        'Individual equipment items ranked by total or average time in use.',
        'You can identify heavily used units that may need maintenance or replacement.'
    ),
    [ChartInfoKind.LONGEST_IN_USE_EQUIPMENTS_LIST]: info(
        ChartInfoKind.LONGEST_IN_USE_EQUIPMENTS_LIST,
        'Equipment currently on loan ranked by how long it has been out.',
        'You can follow up on overdue items and prioritise returns that have been out the longest.'
    ),
    [ChartInfoKind.UNAVAILABLE_PRODUCTS_LIST]: info(
        ChartInfoKind.UNAVAILABLE_PRODUCTS_LIST,
        'Products that currently have no available equipment to lend.',
        'You can see which items users cannot borrow right now and plan restocking or maintenance.'
    ),
    [ChartInfoKind.IOTS_VOLUME_BY_STATUS_DONUT]: info(
        ChartInfoKind.IOTS_VOLUME_BY_STATUS_DONUT,
        'The share of IoT devices by operational status (for example online, offline, or critical).',
        'You can assess overall device fleet health at a glance.'
    ),
    [ChartInfoKind.IOTS_VOLUME_BY_STATUS_BAR]: info(
        ChartInfoKind.IOTS_VOLUME_BY_STATUS_BAR,
        'The count of IoT devices per status category over time or as a snapshot.',
        'You can compare how many devices are in each state and spot shifts in fleet health.'
    ),
    [ChartInfoKind.IOTS_VOLUME_BY_TYPE_LIST]: info(
        ChartInfoKind.IOTS_VOLUME_BY_TYPE_LIST,
        'A ranked list of IoT device counts grouped by device type.',
        'You can see which device types make up your fleet and where concentration lies.'
    ),
    [ChartInfoKind.IOTS_UPTIME_LINE]: info(
        ChartInfoKind.IOTS_UPTIME_LINE,
        'The percentage of time IoT devices were operational over time.',
        'You can track whether fleet reliability is improving or declining.'
    ),
    [ChartInfoKind.IOTS_UPTIME_METRIC]: info(
        ChartInfoKind.IOTS_UPTIME_METRIC,
        'The average IoT uptime percentage for the selected period.',
        'You can quickly gauge overall device reliability for the period.'
    ),
    [ChartInfoKind.IOTS_UPTIME_BY_TYPE_LIST]: info(
        ChartInfoKind.IOTS_UPTIME_BY_TYPE_LIST,
        'IoT uptime percentage ranked by device type.',
        'You can identify which device types are least reliable and may need attention.'
    ),
    [ChartInfoKind.IOTS_UPTIME_WEEKDAY_HEATMAP]: info(
        ChartInfoKind.IOTS_UPTIME_WEEKDAY_HEATMAP,
        'IoT uptime by day of the week over several months, shown as a colour grid.',
        'You can spot recurring weekly patterns in device availability.'
    ),
    [ChartInfoKind.CRITICAL_IOTS_LIST]: info(
        ChartInfoKind.CRITICAL_IOTS_LIST,
        'IoT devices currently in a critical or error state that need immediate attention.',
        'You can prioritise maintenance or replacement for devices that are actively failing.'
    ),
    [ChartInfoKind.CONSUMPTION_VOLUME_BAR]: info(
        ChartInfoKind.CONSUMPTION_VOLUME_BAR,
        'The number of consumption events per time period.',
        'You can track consumption activity over time and compare periods.'
    ),
    [ChartInfoKind.RECYCLES_VOLUME_BAR]: info(
        ChartInfoKind.RECYCLES_VOLUME_BAR,
        'The number of recycle events per time period.',
        'You can monitor recycling activity and spot trends in reuse behaviour.'
    ),
    [ChartInfoKind.REPLENISHMENTS_VOLUME_BAR]: info(
        ChartInfoKind.REPLENISHMENTS_VOLUME_BAR,
        'The number of replenishment events per time period.',
        'You can track restocking activity and correlate it with consumption patterns.'
    ),
    [ChartInfoKind.RETURNS_VOLUME_BAR]: info(
        ChartInfoKind.RETURNS_VOLUME_BAR,
        'The number of return events per time period.',
        'You can monitor return activity over time and identify peak return periods.'
    )
};

export const CHART_CARD_INFO_SECTION_HEADINGS = {
    dataDescription: {
        id: 'Statistics.ChartInfo.DataDescriptionHeading',
        description: 'Section heading in chart card info tooltips explaining what data is shown',
        defaultMessage: 'What am I seeing?'
    },
    conclusions: {
        id: 'Statistics.ChartInfo.ConclusionsHeading',
        description: 'Section heading in chart card info tooltips explaining what conclusions can be drawn',
        defaultMessage: 'What can I conclude?'
    }
} as const;
